// Services / items catalog. Define a service once with default price, GL
// account and tax code, then pick it from a dropdown on every new invoice
// line. Cuts invoice creation time roughly in half.

import { useEffect, useState, type FormEvent } from 'react';
import { Plus, X, Archive, Box } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { formatMoney } from '../lib/format';
import type { Account, Service, TaxCode } from '../lib/types';

export default function Services() {
  const { currentOrg } = useOrg();
  const [services, setServices] = useState<Service[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [taxCodes, setTaxCodes] = useState<TaxCode[]>([]);
  const [editing, setEditing] = useState<Service | 'new' | null>(null);
  const [filter, setFilter] = useState<'sales' | 'purchase'>('sales');

  async function refresh() {
    if (!currentOrg) return;
    const [s, a, tc] = await Promise.all([
      supabase.from('dodobooks_services').select('*').eq('org_id', currentOrg.id).eq('is_archived', false).order('name'),
      supabase.from('dodobooks_accounts').select('*').eq('org_id', currentOrg.id).eq('is_archived', false).order('code'),
      supabase.from('dodobooks_tax_codes').select('*').eq('org_id', currentOrg.id).eq('is_active', true).order('code'),
    ]);
    setServices((s.data ?? []) as Service[]);
    setAccounts((a.data ?? []) as Account[]);
    setTaxCodes((tc.data ?? []) as TaxCode[]);
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [currentOrg]);

  const filtered = services.filter((s) => filter === 'sales' ? !s.is_purchase : s.is_purchase);

  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="h-page">Services & items</h1>
          <p className="text-text-dim text-sm mt-1">Pre-saved invoice lines. Pick from a dropdown when invoicing.</p>
        </div>
        <button onClick={() => setEditing('new')} className="btn btn-primary"><Plus size={14} /> Add service</button>
      </header>

      <div className="flex bg-surface border border-line rounded-md overflow-hidden w-fit">
        {(['sales', 'purchase'] as const).map((f) => (
          <button key={f} type="button" onClick={() => setFilter(f)}
            className={'px-4 py-1.5 text-xs font-mono uppercase tracking-wider ' + (filter === f ? 'bg-emerald/10 text-emerald' : 'text-text-mute hover:text-text')}>
            {f === 'sales' ? 'For invoices' : 'For bills'}
          </button>
        ))}
      </div>

      <div className="card p-0">
        {filtered.length === 0 ? (
          <div className="text-text-mute text-sm py-12 text-center">
            <Box size={28} className="mx-auto text-text-mute mb-3 opacity-50" />
            No services here. <button onClick={() => setEditing('new')} className="text-emerald hover:underline">Add one</button> and they'll appear as a dropdown when creating invoices.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th className="px-5">Code</th>
                <th>Name</th>
                <th>Description</th>
                <th>Account</th>
                <th>Tax</th>
                <th className="text-right pr-5">Default price</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => {
                const acc = accounts.find((a) => a.id === s.default_account_id);
                const tc = taxCodes.find((t) => t.id === s.default_tax_code_id);
                return (
                  <tr key={s.id} onClick={() => setEditing(s)} className="cursor-pointer">
                    <td className="px-5 font-mono text-text-dim">{s.code || '—'}</td>
                    <td className="font-medium">{s.name}</td>
                    <td className="text-text-dim text-xs max-w-md truncate">{s.description || '—'}</td>
                    <td className="text-text-dim font-mono text-xs">{acc ? acc.code + ' ' + acc.name : '—'}</td>
                    <td className="text-text-dim font-mono text-xs">{tc ? tc.code : '—'}</td>
                    <td className="text-right num pr-5">{formatMoney(s.default_price)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <ServiceDialog
          service={editing === 'new' ? null : editing}
          accounts={accounts}
          taxCodes={taxCodes}
          defaultIsPurchase={filter === 'purchase'}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function ServiceDialog({ service, accounts, taxCodes, defaultIsPurchase, onClose, onSaved }: {
  service: Service | null;
  accounts: Account[];
  taxCodes: TaxCode[];
  defaultIsPurchase: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { currentOrg } = useOrg();
  const [form, setForm] = useState({
    code: service?.code ?? '',
    name: service?.name ?? '',
    description: service?.description ?? '',
    default_price: service?.default_price ?? 0,
    default_account_id: service?.default_account_id ?? '',
    default_tax_code_id: service?.default_tax_code_id ?? '',
    is_purchase: service?.is_purchase ?? defaultIsPurchase,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Pre-pick sensible defaults for a new service
  useEffect(() => {
    if (service) return;
    const defaultAcc = accounts.find((a) => a.code === (defaultIsPurchase ? '6900' : '4000'));
    const defaultTax = taxCodes.find((t) => t.code === 'STD');
    setForm((f) => ({
      ...f,
      default_account_id: defaultAcc?.id ?? '',
      default_tax_code_id: defaultTax?.id ?? '',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, taxCodes, defaultIsPurchase]);

  const accountFilter = form.is_purchase
    ? (a: Account) => a.account_type === 'expense' || a.account_type === 'cogs' || a.account_type === 'asset'
    : (a: Account) => a.account_type === 'revenue';
  const taxFilter = form.is_purchase
    ? (t: TaxCode) => t.is_input
    : (t: TaxCode) => t.is_output;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!currentOrg) return;
    setBusy(true); setErr(null);
    const payload = {
      org_id: currentOrg.id,
      code: form.code.trim() || null,
      name: form.name.trim(),
      description: form.description.trim() || null,
      default_price: Number(form.default_price) || 0,
      default_account_id: form.default_account_id || null,
      default_tax_code_id: form.default_tax_code_id || null,
      is_purchase: form.is_purchase,
      updated_at: new Date().toISOString(),
    };
    const op = service
      ? supabase.from('dodobooks_services').update(payload).eq('id', service.id)
      : supabase.from('dodobooks_services').insert(payload);
    const { error } = await op;
    setBusy(false);
    if (error) setErr(error.message);
    else onSaved();
  }

  async function archive() {
    if (!service) return;
    await supabase.from('dodobooks_services').update({ is_archived: true }).eq('id', service.id);
    onSaved();
  }

  return (
    <div className="fixed inset-0 z-50 bg-ink/80 backdrop-blur-sm grid place-items-center px-4">
      <div className="bg-surface border border-line-strong rounded-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-line sticky top-0 bg-surface z-10">
          <h2 className="h-section">{service ? 'Edit service' : 'New service'}</h2>
          <button onClick={onClose} className="text-text-mute hover:text-text"><X size={18} /></button>
        </div>
        <form onSubmit={onSubmit} className="p-5 grid grid-cols-2 gap-4">
          <div className="col-span-2 flex gap-3 items-center bg-ink-2 border border-line rounded-md px-3 py-2">
            <div className="text-xs font-mono uppercase tracking-widest text-text-mute">Used for</div>
            <div className="flex gap-2 ml-auto">
              {(['invoices', 'bills'] as const).map((k) => {
                const isPurchase = k === 'bills';
                const active = form.is_purchase === isPurchase;
                return (
                  <button key={k} type="button" onClick={() => setForm({ ...form, is_purchase: isPurchase })}
                    className={'px-3 py-1 rounded-md text-xs font-mono uppercase tracking-wider ' + (active ? 'bg-emerald text-ink' : 'bg-surface-2 text-text-dim')}>
                    {k}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="field"><label>Code</label><input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. CONSULT-HR" /></div>
          <div className="field"><label>Default price *</label><input required type="number" step="0.01" value={form.default_price} onChange={(e) => setForm({ ...form, default_price: Number(e.target.value) })} /></div>
          <div className="field col-span-2"><label>Name *</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Consulting (hourly)" /></div>
          <div className="field col-span-2"><label>Description (auto-fills onto invoice lines)</label><textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Senior advisory time — strategy, architecture, problem-solving." /></div>
          <div className="field col-span-2">
            <label>GL account</label>
            <select value={form.default_account_id} onChange={(e) => setForm({ ...form, default_account_id: e.target.value })}>
              <option value="">— pick account —</option>
              {accounts.filter(accountFilter).map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
            </select>
          </div>
          <div className="field col-span-2">
            <label>VAT</label>
            <select value={form.default_tax_code_id} onChange={(e) => setForm({ ...form, default_tax_code_id: e.target.value })}>
              <option value="">No VAT</option>
              {taxCodes.filter(taxFilter).map((t) => <option key={t.id} value={t.id}>{t.code} {t.label} ({(t.rate * 100).toFixed(0)}%)</option>)}
            </select>
          </div>

          {err && <div className="col-span-2 text-sm text-rust bg-rust/10 border border-rust/30 px-3 py-2 rounded-md">{err}</div>}

          <div className="col-span-2 flex items-center justify-between pt-2">
            <div>{service && <button type="button" onClick={archive} className="btn btn-ghost text-rust"><Archive size={14} /> Archive</button>}</div>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="btn btn-ghost">Cancel</button>
              <button type="submit" disabled={busy || !form.name.trim()} className="btn btn-primary disabled:opacity-50">{busy ? 'Saving…' : 'Save service'}</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
